import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { PayerStatusFindInterface } from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-find.dto';
import type { PayerStatusFindManyQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-find-many-query.dto';
import { BasisPayerStatusService } from '@zaoblako/web/core/services/basis/basis-payer-status.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

@Component({
  selector: 'crm-payer-status-single-select',
  standalone: true,
  imports: [CommonModule, SpinnerComponent, SingleSelectComponent, SafeHtmlPipe],
  templateUrl: './payer-status-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PayerStatusSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public payerStatuses: PayerStatusFindInterface[] = [];
  public queryParams: PayerStatusFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedPayerStatus: PayerStatusFindInterface;
  @Input() public set selectedPayerStatus(selectedPayerStatus: PayerStatusFindInterface) {
    if (this.customSearch) {
      this._selectedPayerStatus = selectedPayerStatus;
      return;
    }

    if (this.payerStatuses.some((payerStatus) => payerStatus.id === selectedPayerStatus?.id)) {
      this._selectedPayerStatus = this.payerStatuses.find((payerStatus) => payerStatus.id === selectedPayerStatus.id);
      return;
    }

    if (selectedPayerStatus?.id) {
      this.basisPayerStatusService.findById<PayerStatusFindInterface>(selectedPayerStatus.id).subscribe({
        next: (_payerStatus) => {
          if (!this.payerStatuses.some((payerStatus) => payerStatus.id === _payerStatus.id)) {
            this.payerStatuses = this.payerStatuses.concat(_payerStatus);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedPayerStatus = _payerStatus;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedPayerStatusChange = new EventEmitter<PayerStatusFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = false;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedPayerStatusId(id: string) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.payerStatuses.some((payerStatus) => payerStatus.id === id)) {
      this._selectedPayerStatus = this.payerStatuses.find((payerStatus) => payerStatus.id === id);
      return;
    }

    this.basisPayerStatusService.findById<PayerStatusFindInterface>(id).subscribe({
      next: (_payerStatus) => {
        if (!this.payerStatuses.some((payerStatus) => payerStatus.id === _payerStatus.id)) {
          this.payerStatuses = this.payerStatuses.concat(_payerStatus);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedPayerStatus = _payerStatus;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly basisPayerStatusService: BasisPayerStatusService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.basisPayerStatusService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.payerStatuses = this.payerStatuses?.length
            ? this.payerStatuses.concat(
                data.items.filter((item) => !this.payerStatuses.some((payerStatus) => payerStatus.id === item.id))
              )
            : data.items.filter((item) => !this.payerStatuses.some((payerStatus) => payerStatus.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toStatus(status: any): PayerStatusFindInterface {
    return status as PayerStatusFindInterface;
  }

  public onSelectChange(payerStatus: PayerStatusFindInterface) {
    this.selectedPayerStatusChange.emit(payerStatus);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.payerStatuses.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.basisPayerStatusService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.payerStatuses = this.payerStatuses.concat(
                    data.items.filter((item) => !this.payerStatuses.some((payerStatus) => payerStatus.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.basisPayerStatusService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedPayerStatus?.id) && this._selectedPayerStatus) {
            this.payerStatuses = data.items.concat(this._selectedPayerStatus);
          } else {
            this.payerStatuses = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(name: string): string {
    return HighlightMatchTextHelper.highlightMatchText(name, this.singleSelectComponent?.dropdown?.filterValue);
  }
}
